import { Injectable } from "@angular/core";
import { LineSeriesOption } from "echarts/types/dist/shared";
import {
  ECOption,
  IKpiChartDynamicContext,
  IKpiChartPoint,
  IKpiChartStaticContext,
  KpiChartCommentMarkPoint,
  KpiChartCommentMarkPointData,
  KpiChartCommentStyle,
  KpiChartCommentsState,
  projectionPinStylesMap,
  snapshotPinStylesMap,
} from "../kpi-chart.models";
import { isProjectionPoint, isSnapshotPoint } from "../utils";

@Injectable()
export class KpiChartCommentsBuilder {
  private staticContext: IKpiChartStaticContext;
  private dynamicContext: IKpiChartDynamicContext;

  public build(state: KpiChartCommentsState, staticContext: IKpiChartStaticContext, dynamicContext: IKpiChartDynamicContext): LineSeriesOption {
    this.staticContext = staticContext;
    this.dynamicContext = dynamicContext;
    switch (state) {
      case "default":
        return this.buildCommentsInDefaultState();
      case "adding_first_comment":
        return this.buildCommentsWhenAddingFirstCommentToPoint();
      case "any_comment_is_open":
        return this.buildCommentsWhenAnyCommentIsOpen();
      default:
        return;
    }
  }

  public getUpdateProjectionSeriesOnProjectionDrag(staticContext: IKpiChartStaticContext, projectionId: string): LineSeriesOption {
    this.staticContext = staticContext;
    const chartOptions = this.staticContext.chart.getOption() as ECOption;
    const projectionSeries = (chartOptions.series as LineSeriesOption[]).find((s) => s.id === "projectionSeries");
    const projectionMarkPointData = projectionSeries?.markPoint?.data;
    const filteredProjMarkPoints = projectionMarkPointData?.filter((x) => x.value !== projectionId);

    return [
      {
        id: "projectionSeries",
        markPoint: {
          symbol: "pin",
          data: filteredProjMarkPoints,
          animation: false,
        },
      },
    ] as LineSeriesOption;
  }

  private buildCommentsInDefaultState(): LineSeriesOption {
    const snapshotCommentsMarkPointData = this.getSnapshotCommentsMarkPointData("default");
    const projectionCommentsMarkPointData = this.getProjectionCommentsMarkPointData("default");
    return this.buildLineSeriesWithMarkPoints(snapshotCommentsMarkPointData, projectionCommentsMarkPointData);
  }

  private buildCommentsWhenAddingFirstCommentToPoint(): LineSeriesOption {
    const { targetType } = this.dynamicContext.firstCommentContext.pointData;
    if (targetType === "kpi_snapshot") {
      return this.buildCommentsWhenAddingFirstCommentToSnapshot();
    } else if (targetType === "kpi_projection") {
      return this.buildCommentsWhenAddingFirstCommentToProjection();
    }
  }

  private buildCommentsWhenAddingFirstCommentToSnapshot(): LineSeriesOption {
    const snapshotPoint = this.dynamicContext.chartPoints.find(
      (point) => isSnapshotPoint(point) && point.date === this.dynamicContext.firstCommentContext.pointData.targetDate
    );
    const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "snapshotSeries" }, [snapshotPoint.date, snapshotPoint.snapshot]);
    const newCommentPin = this.constructCommentMarkPoint("kpi_snapshot", { name: null, x, y }, "large");
    const existingSnapshotCommentPins = this.getSnapshotCommentsMarkPointData("deemphasized");

    const snapshotCommentsMarkPointData = newCommentPin ? existingSnapshotCommentPins.concat(newCommentPin) : existingSnapshotCommentPins;
    const projectionCommentsMarkPointData = this.getProjectionCommentsMarkPointData("deemphasized");

    return this.buildLineSeriesWithMarkPoints(snapshotCommentsMarkPointData, projectionCommentsMarkPointData);
  }

  private buildCommentsWhenAddingFirstCommentToProjection(): LineSeriesOption {
    const projectionPoint = this.dynamicContext.chartPoints.find(
      (point) => isProjectionPoint(point) && point.date === this.dynamicContext.firstCommentContext.pointData.targetDate
    );
    const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "projectionSeries" }, [projectionPoint.date, projectionPoint.projection]);
    const newCommentPin = this.constructCommentMarkPoint("kpi_projection", { name: null, x, y, value: projectionPoint.projectionId }, "large");
    const existingProjectionCommentPins = this.getProjectionCommentsMarkPointData("deemphasized");

    const snapshotCommentsMarkPointData = this.getSnapshotCommentsMarkPointData("deemphasized");
    const projectionCommentsMarkPointData = newCommentPin ? existingProjectionCommentPins.concat(newCommentPin) : existingProjectionCommentPins;
    return this.buildLineSeriesWithMarkPoints(snapshotCommentsMarkPointData, projectionCommentsMarkPointData);
  }

  private buildCommentsWhenAnyCommentIsOpen(): LineSeriesOption {
    const { targetType } = this.dynamicContext.openComment;
    if (targetType === "kpi_snapshot") {
      return this.buildCommentsWhenSnapshotCommentIsOpen();
    } else if (targetType === "kpi_projection") {
      return this.buildCommentsWhenProjectionCommentIsOpen();
    }
  }

  private buildCommentsWhenSnapshotCommentIsOpen(): LineSeriesOption {
    let snapshotOfOpenComment;
    const snapshotPointsWithComments = this.dynamicContext.chartPoints.filter((point) => {
      const isSnapshotPointWithAComment = isSnapshotPoint(point) && !!this.dynamicContext.snapshotsLeadingCommentsMap[point.date];
      if (isSnapshotPointWithAComment) {
        const isSnapshotOfTheOpenComment = this.dynamicContext.snapshotsLeadingCommentsMap[point.date] === this.dynamicContext.openComment.id;
        if (isSnapshotOfTheOpenComment) {
          snapshotOfOpenComment = point;
        }
        return isSnapshotPointWithAComment;
      }
    });

    const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "snapshotSeries" }, [snapshotOfOpenComment.date, snapshotOfOpenComment.snapshot]);
    const openCommentPin = this.constructCommentMarkPoint("kpi_snapshot", { name: this.dynamicContext.openComment.id, x, y }, "large");
    const snapshotCommentsMarkPointDataWithUnopenedComments = this.getSnapshotCommentsMarkPointDataForPoints(
      "deemphasized",
      snapshotPointsWithComments.filter((x) => x.date !== snapshotOfOpenComment.date)
    );

    const snapshotCommentsMarkPointData = openCommentPin
      ? snapshotCommentsMarkPointDataWithUnopenedComments.concat(openCommentPin)
      : snapshotCommentsMarkPointDataWithUnopenedComments;

    const projectionCommentsMarkPointData = this.getProjectionCommentsMarkPointData("deemphasized");

    return this.buildLineSeriesWithMarkPoints(snapshotCommentsMarkPointData, projectionCommentsMarkPointData);
  }

  private buildCommentsWhenProjectionCommentIsOpen(): LineSeriesOption {
    let projectionOfOpenComment;
    const projectionPointsWithComments = this.dynamicContext.chartPoints.filter((point) => {
      const isProjectionPointWithAComment = isProjectionPoint(point) && !!this.dynamicContext.projectionsLeadingCommentsMap[point.date];
      if (isProjectionPointWithAComment) {
        const isProjectionOfTheOpenComment = this.dynamicContext.projectionsLeadingCommentsMap[point.date] === this.dynamicContext.openComment.id;
        if (isProjectionOfTheOpenComment) {
          projectionOfOpenComment = point;
        }
        return isProjectionPointWithAComment;
      }
    });

    const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "projectionSeries" }, [projectionOfOpenComment?.date, projectionOfOpenComment?.projection]);
    const openCommentPin = this.constructCommentMarkPoint(
      "kpi_projection",
      { name: this.dynamicContext.openComment.id, x, y, value: projectionOfOpenComment.projectionId },
      "large"
    );
    const projectionCommentsMarkPointDataWithUnopenedComments = this.getProjectionCommentsMarkPointDataForPoints(
      "deemphasized",
      projectionPointsWithComments.filter((x) => x.date !== projectionOfOpenComment?.date)
    );

    const snapshotCommentsMarkPointData = this.getSnapshotCommentsMarkPointData("deemphasized");

    const projectionCommentsMarkPointData = openCommentPin
      ? projectionCommentsMarkPointDataWithUnopenedComments.concat(openCommentPin)
      : projectionCommentsMarkPointDataWithUnopenedComments;

    return this.buildLineSeriesWithMarkPoints(snapshotCommentsMarkPointData, projectionCommentsMarkPointData);
  }

  private constructCommentMarkPoint(
    targetType: "kpi_projection" | "kpi_snapshot",
    params: KpiChartCommentMarkPointData,
    style: KpiChartCommentStyle
  ): KpiChartCommentMarkPoint | undefined {
    const isPointInsideGrid = this.staticContext.chart.containPixel("grid", [params.x, params.y]);
    if (isPointInsideGrid) {
      return {
        ...params,
        ...(targetType === "kpi_snapshot" ? snapshotPinStylesMap.get(style) : projectionPinStylesMap.get(style)),
      };
    }
  }

  private getSnapshotCommentsMarkPointData(style: KpiChartCommentStyle): KpiChartCommentMarkPoint[] {
    const snapshotPointsWithComments = this.dynamicContext.chartPoints.filter(
      (point) => isSnapshotPoint(point) && !!this.dynamicContext.snapshotsLeadingCommentsMap[point.date]
    );
    return this.getSnapshotCommentsMarkPointDataForPoints(style, snapshotPointsWithComments);
  }

  private getSnapshotCommentsMarkPointDataForPoints(style: KpiChartCommentStyle, snapshotPointsWithComments: IKpiChartPoint[]): KpiChartCommentMarkPoint[] {
    return snapshotPointsWithComments
      .map((snapshotPoint) => {
        const commentId = this.dynamicContext.snapshotsLeadingCommentsMap[snapshotPoint.date];
        const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "snapshotSeries" }, [snapshotPoint.date, snapshotPoint.snapshot]);
        return this.constructCommentMarkPoint("kpi_snapshot", { name: commentId, x, y }, style);
      })
      .filter((x) => x !== undefined);
  }

  private getProjectionCommentsMarkPointData(style: KpiChartCommentStyle): KpiChartCommentMarkPoint[] {
    const projectionPointsWithComments = this.dynamicContext.chartPoints.filter(
      (point) => isProjectionPoint(point) && !!this.dynamicContext.projectionsLeadingCommentsMap[point.date]
    );
    return this.getProjectionCommentsMarkPointDataForPoints(style, projectionPointsWithComments);
  }

  private getProjectionCommentsMarkPointDataForPoints(style: KpiChartCommentStyle, projectionPointsWithComments: IKpiChartPoint[]): KpiChartCommentMarkPoint[] {
    return projectionPointsWithComments
      .map((projectionPoint) => {
        const commentId = this.dynamicContext.projectionsLeadingCommentsMap[projectionPoint.date];
        const [x, y] = this.staticContext.chart.convertToPixel({ seriesId: "projectionSeries" }, [projectionPoint.date, projectionPoint.projection]);
        return this.constructCommentMarkPoint("kpi_projection", { name: commentId, x, y, value: projectionPoint.projectionId }, style);
      })
      .filter((x) => x !== undefined);
  }

  private buildLineSeriesWithMarkPoints(
    snapshotCommentsMarkPointData: KpiChartCommentMarkPoint[],
    projectionCommentsMarkPointData: KpiChartCommentMarkPoint[]
  ): LineSeriesOption {
    return [
      {
        id: "snapshotSeries",
        markPoint: {
          symbol: "pin",
          data: snapshotCommentsMarkPointData,
          animation: false,
        },
      },

      {
        id: "projectionSeries",
        markPoint: {
          symbol: "pin",
          data: projectionCommentsMarkPointData,
          animation: false,
        },
      },
    ] as LineSeriesOption;
  }
}
